/**
 * ConsolidationCockpitPanel — P0 Sprint (28 Feb 2026, Iteratie 1)
 *
 * Operationeel controlecentrum voor één consolidatie. Vervangt de oude
 * ConsolidationStatusPlanningTab. Toont in één scherm:
 *
 *   §2 Planning        — 12 canonical milestones, elk edit/delete
 *   §3 Automation      — mode + template + start/stop + sync flags
 *   §4 Preview         — dry-run wat automation gaat doen
 *   §5 Shipments       — overview met barcode/QR/HOLD (extended kolommen)
 *   §6 HOLD Manager    — release / keep / exclude per HOLD-zending
 *   §7 Notifications   — per-milestone recipients + channels
 *   §8 Sync            — canonical, geen UI (impliciet via automation)
 *   Timeline           — verticaal, statuskleur (behoud RUN 1 gedrag)
 *
 * Backend endpoint: GET /api/admin/consolidations/{cid}/cockpit
 * (aggregate — één call laadt alles).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Calendar, CheckCircle2, Clock, XCircle, AlertTriangle, Loader2,
  PlayCircle, Ban, RefreshCw, Bell, BellOff, Lock, Unlock, X,
  Zap, ZapOff, Eye, Settings, Save, Copy, ChevronRight, Users,
  Truck, Package, Info, Radio,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const STATE_STYLES = {
  scheduled:  { color: "text-slate-700",  bg: "bg-slate-50 border-slate-200",     icon: Clock,        label: "GEPLAND" },
  executed:   { color: "text-emerald-800", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle2, label: "UITGEVOERD" },
  processing: { color: "text-amber-800",  bg: "bg-amber-50 border-amber-200",     icon: Loader2,      label: "BEZIG" },
  failed:     { color: "text-red-800",    bg: "bg-red-50 border-red-200",         icon: XCircle,      label: "MISLUKT" },
  cancelled:  { color: "text-slate-500",  bg: "bg-slate-50 border-slate-200",     icon: Ban,          label: "GEANNULEERD" },
  skipped:    { color: "text-slate-500",  bg: "bg-slate-50 border-slate-200",     icon: AlertTriangle, label: "OVERGESLAGEN" },
};

const RECIPIENT_LABELS = {
  none: "Geen",
  internal: "Alleen intern",
  agent: "Alleen agent",
  customer: "Alleen klant",
  everyone: "Iedereen",
};
const CHANNEL_LABELS = { email: "Email", tracking: "Tracking", portal: "Portal" };
const MODE_LABELS = {
  handmatig: "Handmatig", automatisch: "Automatisch", hybride: "Hybride",
};

function fmtDateTime(iso, zone = "Europe/Amsterdam") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", year: "numeric",
      ...(String(iso).length > 10 ? { hour: "2-digit", minute: "2-digit" } : {}), timeZone: zone,
    });
  } catch {
    return iso;
  }
}
import { ConsolidationSyncNotice, consolidationStatusLabels } from "../operational/ConsolidationSyncNotice";

function fmtNum(n, digits = 2) {
  return Number(n || 0).toFixed(digits).replace(".", ",");
}
function toDatetimeLocal(iso, zone = "Europe/Amsterdam", dateBoundary = false) {
  if (!iso) return "";
  if (String(iso).length === 10) return dateBoundary ? `${iso}T00:00` : "";
  try {
    const parts = new Intl.DateTimeFormat("sv-SE", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso));
    const p = Object.fromEntries(parts.map(item => [item.type, item.value]));
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch {
    return "";
  }
}
function fromDatetimeLocal(local) {
  if (!local) return null;
  try {
    return local; // The server resolves this clock in the configured IANA zone.
  } catch {
    return null;
  }
}

export default function ConsolidationCockpitPanel({
  consolidationId, data, onReload, onDiagnostics, onSyncReport, diagnosticsTargetId,
}) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("consolidation:status_edit")
                    || hasPermission("tracking:manage");

  const [cockpit, setCockpit] = useState(null);
  const [currentConsolidation, setCurrentConsolidation] = useState(data || {});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [syncReport, setSyncReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, detail] = await Promise.all([
        adminApi.get(`/admin/consolidations/${consolidationId}/cockpit`),
        adminApi.get(`/admin/consolidations/${consolidationId}`),
      ]);
      setCockpit(r.data);
      setCurrentConsolidation(detail.data);
      onDiagnostics?.(detail.data);
    } catch (e) {
      toast.error(formatApiError(e, "Kon cockpit niet laden."));
    } finally {
      setLoading(false);
    }
  }, [consolidationId, onDiagnostics]);
  useEffect(() => { load(); }, [load]);

  const openPreview = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/consolidations/${consolidationId}/automation-preview`, {},
      );
      setPreviewData(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(formatApiError(e, "Preview mislukt."));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !cockpit) {
    return (
      <div className="p-10 text-center text-gray-500" data-testid="cockpit-loading">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Cockpit laden…
      </div>
    );
  }

  return (
    <div className="space-y-4 consolidation-tracking-view" data-testid="consolidation-cockpit">
      <style>{`@media(max-width:1023px){
        .consolidation-tracking-view .tracking-linked-table{display:block;min-width:0;width:100%}
        .consolidation-tracking-view .tracking-linked-table thead,.consolidation-tracking-view .tracking-linked-table tbody{display:block;width:100%}
        .consolidation-tracking-view .tracking-linked-table tr{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));width:100%}
        .consolidation-tracking-view .tracking-linked-table td,.consolidation-tracking-view .tracking-linked-table th{min-width:0;overflow-wrap:anywhere;text-align:left}
        .consolidation-tracking-view .tracking-linked-table td::before{content:attr(data-label);display:block;font:500 10px sans-serif;color:#64748b;margin-bottom:3px}
        .consolidation-tracking-view .tracking-linked-table tr{border-bottom:1px solid #e2e8f0}
      }`}</style>
      {/* Header-stat strip §1 (subset — hoofdheader zit al in ConsolidationDetail) */}
      <StatStrip cockpit={cockpit} />
      <ManualConsolidationStatus consolidationId={consolidationId} consolidation={currentConsolidation}
        cockpit={cockpit} canEdit={canEdit} canResume={hasPermission("consolidation:status_schedule")}
        onResult={report => { setSyncReport(report); onSyncReport?.(report); }}
        onSaved={async () => { await load(); await onReload?.(); }} />

      {/* §2 Planning-board */}
      <PlanningBoard
        consolidationId={consolidationId}
        cockpit={cockpit}
        canEdit={canEdit}
        onResult={report => { setSyncReport(report); onSyncReport?.(report); }}
        onSaved={load}
      />

      {/* §3 Automation config */}
      <AutomationCard
        consolidationId={consolidationId}
        cockpit={cockpit}
        canEdit={canEdit}
        busy={busy}
        onSaved={load}
        onPreview={openPreview}
      />

      {/* §6 HOLD Manager */}
      <HoldManagerCard
        consolidationId={consolidationId}
        cockpit={cockpit}
        canEdit={canEdit}
        onSaved={load}
      />

      {/* §5 Shipments Overview */}
      <ShipmentsOverviewCard cockpit={cockpit} />

      {/* §7 Notification Center */}
      <NotificationCenterCard
        consolidationId={consolidationId}
        cockpit={cockpit}
        canEdit={canEdit}
        onSaved={load}
      />

      {/* Timeline */}
      {!onSyncReport && <ConsolidationSyncNotice report={syncReport || currentConsolidation.shipment_sync} prefix="cockpit-sync" />}
      {diagnosticsTargetId ? (document.getElementById(diagnosticsTargetId) && createPortal(
        <TimelineCard cockpit={cockpit} canEdit={canEdit} onReload={load} />,
        document.getElementById(diagnosticsTargetId))) : <TimelineCard cockpit={cockpit} canEdit={canEdit} onReload={load} />}

      {/* Preview modal */}
      <AutomationPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={previewData}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §1 Stat-strip
// ═══════════════════════════════════════════════════════════════
function StatStrip({ cockpit }) {
  const h = cockpit.header || {};
  const a = cockpit.automation || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs"
         data-testid="cockpit-stat-strip">
      <Stat label="Consolidatie" value={h.consolidation_number} testId="stat-cons-number" />
      <Stat label="Status" value={h.status_label || h.status || "—"} testId="stat-status" />
      <Stat label="Route" value={`${(h.route_departure || "—")} → ${(h.route_destination || "—")}`} testId="stat-route" />
      <Stat label="ETD" value={fmtDateTime(h.etd)} testId="stat-etd" />
      <Stat label="ETA" value={fmtDateTime(h.eta)} testId="stat-eta" />
      <Stat
        label="Automation"
        value={a.enabled ? (a.paused ? "GEPAUZEERD" : `AAN · ${(a.mode || "automatisch").toUpperCase()}`) : "UIT"}
        tone={a.enabled ? (a.paused ? "amber" : "emerald") : "gray"}
        testId="stat-automation"
      />
      <Stat label="Laatste update" value={fmtDateTime(h.updated_at)} testId="stat-updated" />
    </div>
  );
}
function Stat({ label, value, testId, tone }) {
  const toneCls = tone === "emerald" ? "bg-emerald-50 border-emerald-200"
                  : tone === "amber" ? "bg-amber-50 border-amber-200"
                  : tone === "gray" ? "bg-slate-50 border-slate-300"
                  : "bg-slate-50 border-slate-200";
  return (
    <div className={`border px-3 py-2 ${toneCls}`} data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-0.5 text-xs font-bold text-slate-900 truncate">{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §2 Planning board — date-driven tracking, separate confirmations
// ═══════════════════════════════════════════════════════════════
function ManualConsolidationStatus({ consolidationId, consolidation, cockpit, canEdit, canResume, onSaved, onResult }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("vertraagd");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [eta, setEta] = useState("");
  const [reason, setReason] = useState("");
  const [nextStep, setNextStep] = useState("continue");
  const [requestId, setRequestId] = useState("");
  const labels = { ...consolidationStatusLabels(cockpit.milestones_catalog), vertraagd: "Vertraagd" };
  const notice = consolidation.tracking_notice || {};
  const zone = cockpit.automation?.timezone || "Europe/Amsterdam";
  const current = notice.active ? notice.status : consolidation.operational_status || cockpit.header.status;
  const impacted = (cockpit.milestones || []).filter((m) =>
    ["eta", "import_customs", "released", "available_for_pickup", "delivered"].includes(m.key) && m.planned_at);
  const openForm = () => {
    setEffectiveAt(toDatetimeLocal(new Date().toISOString(), zone)); setReason("");
    setEta(toDatetimeLocal(notice.expected_arrival, zone)); setStatus(consolidation.operational_status || cockpit.milestones_catalog?.[0]?.canonical_status || "");
    setNextStep("continue");
    setRequestId(crypto.randomUUID()); setOpen(true);
  };
  const submit = async (resume = false) => {
    if (!resume && (!effectiveAt || ((status === "vertraagd" || nextStep !== "continue") && !reason.trim()))) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/consolidations/${consolidationId}/status-v2`, {
        new_status: resume ? "hervatten" : status,
        reason: resume ? "Automatische planning expliciet hervat door medewerker." : reason.trim(),
        effective_at: resume ? new Date().toISOString() : fromDatetimeLocal(effectiveAt),
        expected_arrival: !resume && status === "vertraagd" && eta ? fromDatetimeLocal(eta) : null,
        pause_automation: !resume && (status === "vertraagd" || nextStep !== "continue"), confirm_entire_consolidation: true,
        request_id: resume ? crypto.randomUUID() : requestId,
      });
      onResult?.(data.shipment_sync);
      setOpen(false); await onSaved();
      if (data.shipment_sync?.complete === false) toast.warning("Update onvolledig. Bekijk de zendingen en blokkaderedenen.");
      else toast.success(resume ? "Automatische planning hervat." : "Consolidatiestatus en gekoppelde zendingen bijgewerkt.");
      if (!resume && nextStep === "plan") document.querySelector('[data-testid="cockpit-planning-board"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) { toast.error(formatApiError(e, "Statuswijziging niet voltooid.")); }
    finally { setBusy(false); }
  };
  return <section className="border-y border-slate-200 py-4" data-testid="manual-consolidation-status">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-sm font-bold text-slate-800">HUIDIGE STATUS HANDMATIG WIJZIGEN</h2>
        <p className="text-sm mt-1" data-testid="manual-current-status">{labels[current] || current || "—"}</p>
        {(notice.active || cockpit.automation.paused) && <p className="text-xs text-amber-800" data-testid="manual-automation-paused">Automatische voortgang gepauzeerd</p>}
        {notice.expected_arrival && <p className="text-xs mt-1" data-testid="manual-current-eta">Nieuwe verwachte aankomst: {fmtDateTime(notice.expected_arrival)}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" className="rounded-none" disabled={!canEdit || busy} onClick={openForm} data-testid="manual-status-open">
          <Settings className="w-4 h-4 mr-2" /> Status wijzigen
        </Button>
        {(notice.active || cockpit.automation.paused) && <Button size="sm" className="rounded-none" disabled={!canEdit || !canResume || busy}
          onClick={() => submit(true)} data-testid="manual-status-resume"><PlayCircle className="w-4 h-4 mr-2" /> Automatisering hervatten</Button>}
      </div>
    </div>
    <Dialog open={open} onOpenChange={(value) => !busy && setOpen(value)}>
      <DialogContent data-testid="manual-status-dialog" style={{ width: "calc(100% - 2rem)", maxWidth: 640, maxHeight: "90dvh", overflowY: "auto" }}>
        <DialogHeader><DialogTitle>Huidige status handmatig wijzigen</DialogTitle>
          <DialogDescription data-testid="manual-status-scope">{cockpit.header.consolidation_number} · volledige consolidatie en actief gekoppelde zendingen</DialogDescription></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
          <div className="min-w-0"><Label htmlFor="manual-status-select">Nieuwe status</Label>
            <select id="manual-status-select" value={status} onChange={(e) => { setStatus(e.target.value); setNextStep(e.target.value === "vertraagd" ? "pause" : "continue"); }}
              className="w-full min-w-0 h-9 border border-slate-300 bg-white px-3 text-sm" data-testid="manual-new-status">
              <optgroup label="Planning">{(cockpit.milestones_catalog || []).map(m => <option key={m.key} value={m.canonical_status} data-testid={`manual-status-option-${m.canonical_status}`}>{m.label_nl}</option>)}</optgroup>
              <optgroup label="Tijdelijke melding"><option value="vertraagd" data-testid="manual-status-option-vertraagd">Vertraagd</option></optgroup>
            </select></div>
          <div className="min-w-0"><Label htmlFor="manual-effective-time">Datum en tijd wijziging</Label>
            <Input id="manual-effective-time" type="datetime-local" className="min-w-0 max-w-full" value={effectiveAt}
              onChange={(e) => setEffectiveAt(e.target.value)} data-testid="manual-effective-at" /></div>
          <div className="sm:col-span-2"><Label htmlFor="manual-reason">Reden / publieke toelichting</Label>
            <Textarea id="manual-reason" value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} data-testid="manual-reason" /></div>
          <div className="min-w-0"><Label htmlFor="manual-expected-time">Nieuwe verwachte aankomst (ETA)</Label>
            <Input id="manual-expected-time" type="datetime-local" className="min-w-0 max-w-full" value={eta} disabled={status !== "vertraagd"}
              onChange={(e) => setEta(e.target.value)} data-testid="manual-expected-arrival" /></div>
          <div className="min-w-0"><Label htmlFor="manual-planning-select">Automatische planning</Label>
            <select id="manual-planning-select" value={nextStep} onChange={(e) => setNextStep(e.target.value)}
              className="w-full min-w-0 h-9 border border-slate-300 bg-white px-3 text-xs" data-testid="manual-automation-action">
              <option value="continue" disabled={status === "vertraagd"} data-testid="manual-action-continue">Automatisch verdergaan</option>
              <option value="pause" data-testid="manual-action-pause">Pauzeren tot expliciet hervatten</option>
              <option value="plan" data-testid="manual-action-edit-plan">Pauzeren en planning aanpassen</option>
            </select></div>
        </div>
        {eta && <div className="border-t pt-3 text-xs" data-testid="manual-eta-impact">
          <p className="font-semibold">Planning bij gewijzigde ETA — datums blijven ongewijzigd</p>
          <ul className="mt-2 space-y-2">{impacted.map((m) => <li key={m.key} className="flex flex-wrap justify-between gap-1" data-testid={`manual-eta-impact-${m.key}`}>
            <span>{m.label_nl} · {fmtDateTime(m.planned_at)}</span>
            <span className="text-amber-800">{m.key === "eta" ? "Oorspronkelijke ETA behouden" : new Date(m.planned_at) < new Date(eta) ? "Vóór nieuwe ETA — controleren" : "Datum ongewijzigd — controleren"}</span>
          </li>)}</ul>
        </div>}
        <DialogFooter className="gap-2"><Button variant="outline" disabled={busy} onClick={() => setOpen(false)} data-testid="manual-status-cancel">Annuleren</Button>
          <Button disabled={busy || !effectiveAt || ((status === "vertraagd" || nextStep !== "continue") && !reason.trim())} onClick={() => submit(false)} data-testid="manual-status-save">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Opslaan
          </Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </section>;
}

function PlanningBoard({ consolidationId, cockpit, canEdit, onSaved, onResult }) {
  const zone = cockpit.automation?.timezone || "Europe/Amsterdam";
  const [confirmationKey, setConfirmationKey] = useState(null);
  const [occurredAt, setOccurredAt] = useState("");
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries((cockpit.milestones || []).map((m) => [m.key, toDatetimeLocal(m.planned_at, zone)]))
  );
  const [notes, setNotes] = useState(() =>
    Object.fromEntries((cockpit.milestones || []).map((m) => [m.key, m.note || ""]))
  );
  const [automatic, setAutomatic] = useState(() => Object.fromEntries(
    (cockpit.milestones || []).map(m => [m.key, m.automatic !== false])));
  const { hasPermission } = useAuth();
  const canSchedule = hasPermission("consolidation:status_schedule");
  const [saving, setSaving] = useState(false);

  const dirtyKeys = useMemo(() => {
    const dirty = new Set();
    (cockpit.milestones || []).forEach((m) => {
      const curLocal = toDatetimeLocal(m.planned_at, zone);
      if ((drafts[m.key] || "") !== (curLocal || "")) dirty.add(m.key);
      if ((notes[m.key] || "") !== (m.note || "")) dirty.add(m.key);
      if (automatic[m.key] !== (m.automatic !== false)) dirty.add(m.key);
    });
    return dirty;
  }, [drafts, notes, automatic, cockpit.milestones, zone]);

  const saveAll = async () => {
    if (dirtyKeys.size === 0) return;
    setSaving(true);
    try {
      const payload = { milestones: [] };
      for (const key of dirtyKeys) {
        const original = cockpit.milestones.find(m => m.key === key);
        payload.milestones.push({
          key,
          ...((drafts[key] || "") !== toDatetimeLocal(original.planned_at, zone) ? { planned_at: fromDatetimeLocal(drafts[key]) } : {}),
          ...((notes[key] || "") !== (original.note || "") ? { note: notes[key] || null } : {}),
          ...(automatic[key] !== (original.automatic !== false) ? { automatic: automatic[key] } : {}),
        });
      }
      const { data } = await adminApi.patch(
        `/admin/consolidations/${consolidationId}/milestones`, payload,
      );
      const sync = data?.shipment_sync || {};
      onResult?.(sync);
      const blocked = (sync.triggered || []).filter(row => !["applied", "idempotent", "noop"].includes(row.result));
      if (sync.complete === false || blocked.length || sync.error || sync.retryable || sync.skipped_reason) toast.warning("Planning opgeslagen; niet alle statusovergangen zijn uitgevoerd. Controleer de planning en de taakreden.");
      else toast.success(`${dirtyKeys.size} milestone(s) opgeslagen.`);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan milestones mislukt."));
    } finally {
      setSaving(false);
    }
  };

  const deleteOne = async (key) => {
    setSaving(true);
    try {
      await adminApi.patch(
        `/admin/consolidations/${consolidationId}/milestones`,
        { milestones: [{ key, delete: true }] },
      );
      toast.success("Milestone verwijderd.");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Verwijderen mislukt."));
    } finally {
      setSaving(false);
    }
  };

  const confirmOccurrence = async (key) => {
    const occurred = fromDatetimeLocal(occurredAt);
    if (!occurred) {
      toast.error("Kies het werkelijke tijdstip, niet een toekomstige planning.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.patch(`/admin/consolidations/${consolidationId}/milestones`, {
        milestones: [{ key, occurred_at: occurred }],
      });
      const sync = data?.shipment_sync || {};
      onResult?.(sync);
      const incomplete = sync.complete === false || sync.error || sync.skipped_reason || (sync.triggered || []).some((r) => !["applied", "idempotent", "noop"].includes(r.result));
      if (incomplete) toast.warning("Gebeurtenis opgeslagen; statusverwerking nog niet voltooid.");
      else toast.success("Gebeurtenis bevestigd.");
      setConfirmationKey(null);
      await onSaved();
    } catch (e) { toast.error(formatApiError(e, "Bevestigen mislukt.")); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="cockpit-planning-board">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex flex-wrap gap-2 items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
          <Calendar className="w-4 h-4" /> Planning ({cockpit.milestones?.length || 0} milestones)
        </div>
        {canEdit && (
          <Button
            size="sm" onClick={saveAll} disabled={saving || dirtyKeys.size === 0}
            className="rounded-none text-xs bg-[#082c50] hover:bg-[#0a3b6a] text-white"
            data-testid="cockpit-planning-save-all"
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Opslaan ({dirtyKeys.size})
          </Button>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {(cockpit.milestones || []).map((m) => {
          const dirty = dirtyKeys.has(m.key);
          const hasDate = !!m.planned_at;
          return (
            <div key={m.key}
                 className={`grid grid-cols-12 gap-2 items-center px-4 py-2 text-xs min-w-0 ${dirty ? "bg-amber-50/40" : ""}`}
                 data-testid={`milestone-row-${m.key}`}>
              <div className="col-span-12 sm:col-span-3 min-w-0 flex items-center gap-2">
                <span className={`inline-block shrink-0 w-2 h-2 rounded-full ${m.confirmed ? "bg-emerald-500" : hasDate ? "bg-blue-400" : "bg-slate-300"}`} />
                <span className="font-bold text-slate-800">{m.label_nl}</span>
              </div>
              <div className="col-span-12 sm:col-span-3 min-w-0 break-words text-[10px] text-slate-500 font-mono">
                {m.canonical_status ? `→ ${m.canonical_status}` : <span className="italic">geen status-mutatie</span>}
              </div>
              <div className="col-span-12 sm:col-span-3 min-w-0">
                <Input
                  type="datetime-local"
                  value={drafts[m.key] || ""}
                  onChange={(e) => setDrafts((s) => ({ ...s, [m.key]: e.target.value }))}
                  disabled={!canEdit || saving}
                  className="rounded-none h-8 text-xs min-w-0 max-w-full"
                  data-testid={`milestone-date-${m.key}`}
                />
              </div>
              <div className="col-span-10 sm:col-span-2 min-w-0">
                <Input
                  placeholder="Notitie"
                  value={notes[m.key] || ""}
                  onChange={(e) => setNotes((s) => ({ ...s, [m.key]: e.target.value }))}
                  disabled={!canEdit || saving}
                  className="rounded-none h-8 text-xs"
                  data-testid={`milestone-note-${m.key}`}
                  maxLength={200}
                />
              </div>
              <div className="col-span-2 sm:col-span-1 flex justify-end">
                {canEdit && hasDate && !m.confirmed && (
                  <Button
                    size="sm" variant="ghost"
                    className="rounded-none h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                    onClick={() => deleteOne(m.key)}
                    disabled={saving}
                    data-testid={`milestone-delete-${m.key}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <label className="col-span-12 flex items-center gap-2 text-xs min-w-0" data-testid={`milestone-auto-label-${m.key}`}>
                <Switch checked={!!automatic[m.key]} onCheckedChange={value => setAutomatic(current => ({ ...current, [m.key]: value }))}
                  disabled={!canSchedule || saving} aria-label={`${m.label_nl} automatisch`}
                  data-testid={`milestone-auto-${m.key}`} /> Automatisch
              </label>
              {m.canonical_status && m.key !== "etd" && !m.protected_decision && (
                <div className="col-span-12 min-w-0 flex flex-wrap items-center gap-2">
                  {m.confirmed ? (
                    <span className="text-xs text-emerald-700 break-words" data-testid={`milestone-confirmed-${m.key}`}>
                      Bevestigd · {fmtDateTime(m.occurred_at, zone)}
                    </span>
                  ) : canEdit && confirmationKey !== m.key ? (
                    <Button type="button" size="sm" variant="outline" className="rounded-none text-xs"
                      disabled={saving} data-testid={`milestone-confirm-open-${m.key}`}
                      onClick={() => { setConfirmationKey(m.key); setOccurredAt(toDatetimeLocal(new Date().toISOString(), zone)); }}>
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Als bevestigd registreren
                    </Button>
                  ) : canEdit && confirmationKey === m.key ? (
                    <>
                      <input type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)}
                        aria-label="Werkelijk tijdstip" data-testid={`milestone-occurred-at-${m.key}`}
                        className="min-w-0 max-w-full border border-slate-300 text-xs p-2" />
                      <Button type="button" size="sm" className="rounded-none text-xs" disabled={saving || !occurredAt}
                        data-testid={`milestone-confirm-submit-${m.key}`} onClick={() => confirmOccurrence(m.key)}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Bevestigen
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="rounded-none text-xs"
                        data-testid={`milestone-confirm-cancel-${m.key}`} onClick={() => setConfirmationKey(null)}>
                        Annuleren
                      </Button>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §3 Automation card
// ═══════════════════════════════════════════════════════════════
function AutomationCard({ consolidationId, cockpit, canEdit, busy, onSaved, onPreview }) {
  const a = cockpit.automation || {};
  const [form, setForm] = useState(() => ({
    enabled: !!a.enabled,
    mode: a.mode || "automatisch",
    template_code: a.template_code || "",
    start_at: toDatetimeLocal(a.start_at, a.timezone || "Europe/Amsterdam", true),
    stop_at: toDatetimeLocal(a.stop_at, a.timezone || "Europe/Amsterdam", true),
    timezone: a.timezone || "Europe/Amsterdam",
    tracking_updates: a.tracking_updates !== false,
    customer_notifications: a.customer_notifications !== false,
    portal_updates: a.portal_updates !== false,
    warehouse_sync: a.warehouse_sync !== false,
  }));
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const modes = cockpit.notification_options?.automation_modes
                  || ["handmatig", "automatisch", "hybride"];

  const save = async () => {
    setSaving(true);
    try {
      const { data: outcome } = await adminApi.patch(
        `/admin/consolidations/${consolidationId}/automation-config`,
        {
          enabled: !!form.enabled,
          mode: form.mode,
          template_code: form.template_code || null,
          ...(form.start_at !== toDatetimeLocal(a.start_at, a.timezone || "Europe/Amsterdam", true) ? { start_at: fromDatetimeLocal(form.start_at) } : {}),
          ...(form.stop_at !== toDatetimeLocal(a.stop_at, a.timezone || "Europe/Amsterdam", true) ? { stop_at: fromDatetimeLocal(form.stop_at) } : {}),
          timezone: form.timezone || null,
          tracking_updates: !!form.tracking_updates,
          customer_notifications: !!form.customer_notifications,
          portal_updates: !!form.portal_updates,
          warehouse_sync: !!form.warehouse_sync,
        },
      );
      if (outcome.shipment_sync?.complete === false) toast.warning("Automation opgeslagen; een milestone wacht op verwerking. Zie onderaan de pagina.");
      else toast.success("Automation config opgeslagen.");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="cockpit-automation-card">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
          {form.enabled ? <Zap className="w-4 h-4 text-emerald-600" /> : <ZapOff className="w-4 h-4 text-slate-400" />}
          Consolidation Automation
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline"
                  onClick={onPreview} disabled={busy || !form.template_code}
                  className="rounded-none text-xs"
                  data-testid="cockpit-automation-preview-btn">
            <Eye className="w-3 h-3 mr-1" /> Preview
          </Button>
          {canEdit && (
            <Button size="sm"
                    onClick={save} disabled={saving}
                    className="rounded-none text-xs bg-[#082c50] hover:bg-[#0a3b6a] text-white"
                    data-testid="cockpit-automation-save">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Opslaan
            </Button>
          )}
        </div>
      </div>
      <div className="p-4 space-y-4 text-xs">
        {/* Aan/Uit + Mode + Template */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Automation status">
            <div className="flex items-center gap-2 pt-1.5">
              <Switch checked={form.enabled}
                      onCheckedChange={(v) => setF("enabled", v)}
                      disabled={!canEdit}
                      data-testid="cockpit-automation-toggle" />
              <span className={`text-xs font-bold ${form.enabled ? "text-emerald-700" : "text-slate-500"}`}>
                {form.enabled ? "AAN" : "UIT"}
              </span>
              {a.paused && form.enabled && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 uppercase font-bold">
                  gepauzeerd
                </span>
              )}
            </div>
          </Field>
          <Field label="Mode">
            <Select value={form.mode} onValueChange={(v) => setF("mode", v)} disabled={!canEdit}>
              <SelectTrigger className="rounded-none h-8" data-testid="cockpit-automation-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {modes.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`cockpit-automation-mode-${m}`}>
                    {MODE_LABELS[m] || m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Statusworkflow">
            <p className="text-sm py-1" data-testid="cockpit-automation-workflow">PLANNING (12 MILESTONES)</p>
          </Field>
        </div>

        {/* Start / Stop / Timezone */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Automation start">
            <Input type="datetime-local" value={form.start_at}
                   onChange={(e) => setF("start_at", e.target.value)}
                   disabled={!canEdit}
                   className="rounded-none h-8"
                   data-testid="cockpit-automation-start-at" />
          </Field>
          <Field label="Automation stop">
            <Input type="datetime-local" value={form.stop_at}
                   onChange={(e) => setF("stop_at", e.target.value)}
                   disabled={!canEdit}
                   className="rounded-none h-8"
                   data-testid="cockpit-automation-stop-at" />
          </Field>
          <Field label="Tijdzone">
            <Input value={form.timezone}
                   onChange={(e) => setF("timezone", e.target.value)}
                   disabled={!canEdit}
                   placeholder="Europe/Amsterdam"
                   className="rounded-none h-8 font-mono text-xs"
                   data-testid="cockpit-automation-timezone" />
          </Field>
        </div>

        {/* Sync flags */}
        <div className="border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] uppercase font-bold text-slate-600 mb-2 tracking-wide">
            Synchronisatie targets · Set it &amp; Forget it
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <SyncToggle label="Tracking updates" checked={form.tracking_updates}
                        onChange={(v) => setF("tracking_updates", v)}
                        disabled={!canEdit}
                        testId="cockpit-sync-tracking" />
            <SyncToggle label="Customer notifications" checked={form.customer_notifications}
                        onChange={(v) => setF("customer_notifications", v)}
                        disabled={!canEdit}
                        testId="cockpit-sync-customer" />
            <SyncToggle label="Customer Portal" checked={form.portal_updates}
                        onChange={(v) => setF("portal_updates", v)}
                        disabled={!canEdit}
                        testId="cockpit-sync-portal" />
            <SyncToggle label="Warehouse sync" checked={form.warehouse_sync}
                        onChange={(v) => setF("warehouse_sync", v)}
                        disabled={!canEdit}
                        testId="cockpit-sync-warehouse" />
          </div>
        </div>

        {a.last_updated_at && (
          <div className="text-[10px] text-slate-500 italic">
            Laatst gewijzigd: {fmtDateTime(a.last_updated_at)} · door {a.last_updated_by || "—"}
          </div>
        )}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold tracking-wide text-slate-600">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function SyncToggle({ label, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-slate-200 px-2 py-1.5">
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled}
              data-testid={testId} />
      <span className="text-[11px] text-slate-800">{label}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §6 HOLD Manager
// ═══════════════════════════════════════════════════════════════
function HoldManagerCard({ consolidationId, cockpit, canEdit, onSaved }) {
  const holds = cockpit.hold_shipments || [];
  const [busy, setBusy] = useState(null); // link_id in progress

  const decide = async (linkId, decision, reason) => {
    setBusy(linkId);
    try {
      await adminApi.post(
        `/admin/consolidations/${consolidationId}/shipments/${linkId}/hold-decision`,
        { decision, reason: reason || null },
      );
      toast.success(`HOLD-beslissing '${decision}' opgeslagen.`);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Beslissing opslaan mislukt."));
    } finally {
      setBusy(null);
    }
  };

  if (holds.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-2 text-xs"
           data-testid="cockpit-hold-manager-empty">
        <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        <span className="text-emerald-900 font-bold">Geen zendingen op HOLD.</span>
        <span className="text-emerald-800">Alle {cockpit.shipments_total} zending(en) klaar voor automatisch vertrek.</span>
      </div>
    );
  }
  return (
    <div className="bg-white border border-red-200" data-testid="cockpit-hold-manager">
      <div className="px-4 py-2 border-b border-red-200 bg-red-50 flex items-center gap-2">
        <Lock className="w-4 h-4 text-red-700" />
        <div className="text-xs font-bold uppercase tracking-wide text-red-900">
          HOLD Manager — {holds.length} zending{holds.length === 1 ? "" : "en"} geblokkeerd
        </div>
      </div>
      <div className="p-3 text-xs text-red-900 border-b border-red-100 bg-red-50/50">
        <AlertTriangle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
        Automation neemt deze zendingen niet mee bij vertrek. Kies per zending: <strong>Vrijgeven</strong>, <strong>HOLD behouden</strong>, of <strong>Niet meenemen</strong>.
      </div>
      <div className="divide-y divide-red-100">
        {holds.map((h) => (
          <HoldRow key={h.link_id} h={h} busy={busy === h.link_id} canEdit={canEdit}
                   onDecide={(d, r) => decide(h.link_id, d, r)} />
        ))}
      </div>
    </div>
  );
}
function HoldRow({ h, busy, canEdit, onDecide }) {
  const [confirming, setConfirming] = useState(null); // "release"|"keep"|"exclude"|null
  const [reason, setReason] = useState("");
  const current = h.hold_decision;
  const openConfirm = (d) => {
    setConfirming(d);
    setReason("");
  };
  const submit = () => {
    if (!confirming) return;
    onDecide(confirming, reason);
    setConfirming(null);
  };
  return (
    <div className="p-3 grid grid-cols-12 gap-2 items-center text-xs"
         data-testid={`hold-row-${h.link_id}`}>
      <div className="col-span-3">
        <div className="font-mono font-bold text-red-900">{h.w_number}</div>
        <div className="text-[10px] text-slate-500">{h.service_type} · {h.country_name}</div>
      </div>
      <div className="col-span-3">
        <div className="text-slate-800">{h.recipient_name || "—"}</div>
        <div className="text-[10px] text-slate-500">{h.recipient_city}</div>
      </div>
      <div className="col-span-2 font-mono">
        <div>{fmtNum(h.weight_kg, 1)} kg · {h.piece_count} colli</div>
        <div className="text-[10px] text-slate-500">CBM {fmtNum(h.cbm, 4)}</div>
      </div>
      <div className="col-span-1">
        <span className="inline-block text-[10px] uppercase font-bold px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-200">
          {h.payment_status || "?"}
        </span>
      </div>
      <div className="col-span-3 flex flex-wrap gap-1 justify-end">
        {current && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 border uppercase ${
            current === "release" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : current === "exclude" ? "bg-slate-200 text-slate-800 border-slate-300"
            : "bg-amber-100 text-amber-800 border-amber-200"
          }`}
                data-testid={`hold-row-decision-${h.link_id}`}>
            {current === "release" ? "VRIJGEGEVEN"
              : current === "exclude" ? "NIET MEE" : "HOLD"}
          </span>
        )}
        {canEdit && (
          <>
            <Button size="sm" onClick={() => openConfirm("release")}
                    disabled={busy}
                    className="h-6 px-2 rounded-none text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`hold-row-release-${h.link_id}`}>
              <Unlock className="w-3 h-3 mr-1" /> Vrijgeven
            </Button>
            <Button size="sm" variant="outline" onClick={() => openConfirm("keep")}
                    disabled={busy}
                    className="h-6 px-2 rounded-none text-[10px] border-amber-300 text-amber-800"
                    data-testid={`hold-row-keep-${h.link_id}`}>
              <Lock className="w-3 h-3 mr-1" /> HOLD
            </Button>
            <Button size="sm" variant="outline" onClick={() => openConfirm("exclude")}
                    disabled={busy}
                    className="h-6 px-2 rounded-none text-[10px] border-slate-300 text-slate-700"
                    data-testid={`hold-row-exclude-${h.link_id}`}>
              <X className="w-3 h-3 mr-1" /> Niet mee
            </Button>
          </>
        )}
      </div>
      {confirming && (
        <div className="col-span-12 mt-2 border border-slate-200 bg-slate-50 p-2 flex items-end gap-2"
             data-testid={`hold-row-confirm-${h.link_id}`}>
          <div className="flex-1">
            <div className="text-[10px] uppercase font-bold text-slate-600 mb-1">
              Reden voor &apos;{confirming === "release" ? "vrijgeven" : confirming === "exclude" ? "niet meenemen" : "HOLD behouden"}&apos; (optioneel)
            </div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                   className="rounded-none h-8 text-xs bg-white"
                   maxLength={500}
                   placeholder="bv. betaling ontvangen, wacht op factuur, ..."
                   data-testid={`hold-row-reason-${h.link_id}`} />
          </div>
          <Button size="sm" onClick={submit}
                  className="h-8 rounded-none text-xs bg-[#082c50] text-white"
                  data-testid={`hold-row-confirm-btn-${h.link_id}`}>
            Bevestigen
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}
                  className="h-8 rounded-none text-xs">
            Annuleren
          </Button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §5 Shipments Overview
// ═══════════════════════════════════════════════════════════════
function ShipmentsOverviewCard({ cockpit }) {
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState("w_number");
  const [sortDir, setSortDir] = useState("asc");
  const rows = useMemo(() => {
    let list = (cockpit.shipments || []).slice();
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      list = list.filter((r) =>
        (r.w_number || "").toLowerCase().includes(q)
        || (r.recipient_name || "").toLowerCase().includes(q)
        || (r.sender_name || "").toLowerCase().includes(q)
        || (r.country_name || "").toLowerCase().includes(q)
        || (r.payment_status || "").toLowerCase().includes(q));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sortBy] ?? "";
      const bv = b[sortBy] ?? "";
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [cockpit.shipments, filter, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };
  const sortIndicator = (k) => (sortBy === k ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  return (
    <div className="bg-white border border-slate-200" data-testid="cockpit-shipments">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
          <Package className="w-4 h-4" /> Zendingen ({cockpit.shipments_total || 0})
        </div>
        <Input placeholder="Filter W-nummer / naam / land / betaal…"
               value={filter} onChange={(e) => setFilter(e.target.value)}
               className="rounded-none h-8 text-xs max-w-xs"
               data-testid="cockpit-shipments-filter" />
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-500 italic">
          Nog geen zendingen gekoppeld.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tracking-linked-table w-full text-xs table-fixed break-words">
            <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 border-b border-slate-200">
              <tr>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("w_number")} data-testid="shipments-sort-w_number">W-nummer{sortIndicator("w_number")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("service_type")} data-testid="shipments-sort-service_type">Service{sortIndicator("service_type")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("country_name")} data-testid="shipments-sort-country_name">Land{sortIndicator("country_name")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("sender_name")} data-testid="shipments-sort-sender_name">Afzender{sortIndicator("sender_name")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("recipient_name")} data-testid="shipments-sort-recipient_name">Ontvanger{sortIndicator("recipient_name")}</th>
                <th className="text-right p-2 cursor-pointer select-none" onClick={() => toggleSort("piece_count")} data-testid="shipments-sort-piece_count">Colli{sortIndicator("piece_count")}</th>
                <th className="text-right p-2 cursor-pointer select-none" onClick={() => toggleSort("weight_kg")} data-testid="shipments-sort-weight_kg">Gewicht{sortIndicator("weight_kg")}</th>
                <th className="text-right p-2 cursor-pointer select-none" onClick={() => toggleSort("cbm")} data-testid="shipments-sort-cbm">CBM{sortIndicator("cbm")}</th>
                <th className="text-right p-2 cursor-pointer select-none" onClick={() => toggleSort("declared_value")} data-testid="shipments-sort-declared_value">Waarde{sortIndicator("declared_value")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("payment_status")} data-testid="shipments-sort-payment_status">Betaal{sortIndicator("payment_status")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("release_status")} data-testid="shipments-sort-release_status">Release{sortIndicator("release_status")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("warehouse_status")} data-testid="shipments-sort-warehouse_status">Warehouse{sortIndicator("warehouse_status")}</th>
                <th className="text-left p-2 cursor-pointer select-none" onClick={() => toggleSort("tracking_status")} data-testid="shipments-sort-tracking_status">Tracking{sortIndicator("tracking_status")}</th>
                <th className="text-center p-2">HOLD</th>
                <th className="text-center p-2">Notify</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((s) => (
                <tr key={s.link_id}
                    className={`hover:bg-slate-50 ${s.hold_flag ? "bg-red-50/30" : ""}`}
                    data-testid={`shipment-row-${s.link_id}`}>
                  <td data-label="W-nummer" className="p-2 font-mono font-bold text-[#082c50]">
                    {s.w_number}
                    {s.qr_url && (
                      <button
                        type="button"
                        className="ml-1 text-slate-400 hover:text-slate-700"
                        title={s.qr_url}
                        onClick={() => { navigator.clipboard.writeText(s.qr_url); toast.success("QR-URL gekopieerd"); }}
                        data-testid={`shipment-qr-${s.link_id}`}
                      >
                        <Radio className="inline w-3 h-3" />
                      </button>
                    )}
                  </td>
                  <td data-label="Service" className="p-2 uppercase text-[10px] font-bold">{s.service_type || "—"}</td>
                  <td data-label="Land" className="p-2">{s.country_name || "—"}</td>
                  <td data-label="Afzender" className="p-2">
                    {s.sender_name || "—"}
                    {s.sender_city && <div className="text-[10px] text-slate-500">{s.sender_city}</div>}
                  </td>
                  <td data-label="Ontvanger" className="p-2">
                    {s.recipient_name || "—"}
                    {s.recipient_city && <div className="text-[10px] text-slate-500">{s.recipient_city}</div>}
                  </td>
                  <td data-label="Colli" className="p-2 text-right font-mono">{s.piece_count}</td>
                  <td data-label="Gewicht" className="p-2 text-right font-mono">{fmtNum(s.weight_kg, 1)}</td>
                  <td data-label="CBM" className="p-2 text-right font-mono">{fmtNum(s.cbm, 4)}</td>
                  <td data-label="Waarde" className="p-2 text-right font-mono">
                    {s.declared_value > 0 ? `${s.declared_value_currency} ${fmtNum(s.declared_value)}` : "—"}
                  </td>
                  <td data-label="Betaal" className="p-2 uppercase text-[10px] font-bold">{s.payment_status || "—"}</td>
                  <td data-label="Release" className="p-2 uppercase text-[10px] font-bold">
                    <span className={s.release_status === "hold" ? "text-red-700" : "text-emerald-700"}>
                      {s.release_status || "—"}
                    </span>
                  </td>
                  <td data-label="Warehouse" className="p-2 text-[10px]">{s.warehouse_status || "—"}</td>
                  <td data-label="Tracking" className="p-2 text-[10px]">{s.tracking_status || "—"}</td>
                  <td data-label="HOLD" className="p-2 text-center">
                    {s.hold_flag ? (
                      <span className="inline-block text-[10px] px-1.5 py-0.5 bg-red-600 text-white font-bold uppercase">HOLD</span>
                    ) : (
                      <CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-600" />
                    )}
                    {s.hold_decision === "exclude" && (
                      <div className="text-[9px] text-slate-500 uppercase mt-0.5">niet mee</div>
                    )}
                  </td>
                  <td data-label="Notify" className="p-2 text-center">
                    {s.notify_customer !== false
                      ? <Bell className="inline w-3.5 h-3.5 text-emerald-600" />
                      : <BellOff className="inline w-3.5 h-3.5 text-slate-400" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §7 Notification Center
// ═══════════════════════════════════════════════════════════════
function NotificationCenterCard({ consolidationId, cockpit, canEdit, onSaved }) {
  const notifCfg = cockpit.notification_config || {};
  const options = cockpit.notification_options || {};
  const scopes = options.recipient_scopes || ["none", "internal", "agent", "customer", "everyone"];
  const channels = options.channels || ["email", "tracking", "portal"];
  const milestones = cockpit.milestones || [];
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(milestones.map((m) => [m.key, {
      recipients: notifCfg[m.key]?.recipients || "internal",
      channels: notifCfg[m.key]?.channels || ["tracking"],
    }]))
  );
  const [saving, setSaving] = useState(false);
  const dirty = useMemo(() => {
    return milestones.some((m) => {
      const cur = notifCfg[m.key] || { recipients: "internal", channels: ["tracking"] };
      const d = drafts[m.key];
      if (!d) return false;
      if (d.recipients !== cur.recipients) return true;
      const a = (d.channels || []).slice().sort().join(",");
      const b = (cur.channels || []).slice().sort().join(",");
      return a !== b;
    });
  }, [drafts, notifCfg, milestones]);

  const toggleChannel = (key, ch) => {
    setDrafts((s) => {
      const cur = s[key] || { recipients: "internal", channels: [] };
      const has = cur.channels.includes(ch);
      return {
        ...s,
        [key]: { ...cur, channels: has ? cur.channels.filter((c) => c !== ch) : [...cur.channels, ch] },
      };
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const entries = {};
      milestones.forEach((m) => {
        entries[m.key] = drafts[m.key];
      });
      await adminApi.patch(
        `/admin/consolidations/${consolidationId}/notification-config`,
        { entries },
      );
      toast.success("Notification-config opgeslagen.");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200" data-testid="cockpit-notification-center">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
          <Bell className="w-4 h-4" /> Notification Center
        </div>
        {canEdit && (
          <Button size="sm" onClick={save} disabled={saving || !dirty}
                  className="rounded-none text-xs bg-[#082c50] hover:bg-[#0a3b6a] text-white"
                  data-testid="cockpit-notif-save">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Opslaan
          </Button>
        )}
      </div>
      <div className="p-3 text-[10px] text-slate-500 italic bg-slate-50/50 border-b border-slate-100">
        <Info className="inline w-3 h-3 mr-1 -mt-0.5" />
        Per milestone kies je wie een update krijgt (geen/intern/agent/klant/iedereen) en via welke kanalen (email/tracking/portal). Wordt automatisch toegepast door de scheduler bij event-execution.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs table-fixed break-words">
          <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 border-b border-slate-200">
            <tr>
              <th className="text-left p-2">Milestone</th>
              <th className="text-left p-2">Ontvangers</th>
              {channels.map((ch) => (
                <th key={ch} className="text-center p-2">{CHANNEL_LABELS[ch] || ch}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {milestones.map((m) => {
              const d = drafts[m.key] || { recipients: "internal", channels: [] };
              return (
                <tr key={m.key} data-testid={`notif-row-${m.key}`}>
                  <td className="p-2 font-bold text-slate-800">{m.label_nl}</td>
                  <td className="p-2">
                    <Select value={d.recipients}
                            onValueChange={(v) => setDrafts((s) => ({ ...s, [m.key]: { ...s[m.key], recipients: v } }))}
                            disabled={!canEdit}>
                      <SelectTrigger className="rounded-none h-7 text-xs w-40"
                                     data-testid={`notif-recipients-${m.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {scopes.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`notif-recipients-${m.key}-${s}`}>
                            {RECIPIENT_LABELS[s] || s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  {channels.map((ch) => (
                    <td key={ch} className="p-2 text-center">
                      <input type="checkbox"
                             checked={d.channels.includes(ch)}
                             disabled={!canEdit || d.recipients === "none"}
                             onChange={() => toggleChannel(m.key, ch)}
                             data-testid={`notif-channel-${m.key}-${ch}`} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Timeline (behoud RUN 1)
// ═══════════════════════════════════════════════════════════════
function TimelineCard({ cockpit, canEdit, onReload }) {
  const events = cockpit.timeline || [];
  const rows = useMemo(() => {
    const r = events.map((ev) => ({
      state: ev.state || "scheduled",
      status: ev.new_status || ev.status,
      when: ev.executed_at || ev.scheduled_for,
      planned: ev.scheduled_for,
      source: ev.rule_type,
      notify: ev.notify_customer,
      error: ev.state_reason || ev.error_message,
      id: ev.id,
    }));
    r.sort((a, b) => new Date(a.when || 0) - new Date(b.when || 0));
    return r;
  }, [events]);
  const retryEvent = async (eventId) => {
    try {
      await adminApi.post(
        `/admin/consolidation-automation/events/${eventId}/retry`, {},
      );
      toast.success("Event opnieuw gepland.");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e, "Retry mislukt."));
    }
  };
  return (
    <div className="bg-white border border-slate-200" data-testid="cockpit-timeline">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Tijdlijn ({rows.length})
        </div>
        <Button size="sm" variant="ghost" onClick={onReload}
                className="rounded-none text-xs" data-testid="cockpit-timeline-reload">
          <RefreshCw className="w-3 h-3 mr-1" /> Herladen
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center italic">
          Nog geen ingeplande uitvoering.
        </div>
      ) : (
        <ol className="divide-y divide-slate-100">
          {rows.map((row, idx) => {
            const meta = STATE_STYLES[row.state] || STATE_STYLES.scheduled;
            const Icon = meta.icon;
            return (
              <li key={row.id || idx}
                  className={`flex items-start gap-3 px-4 py-3 ${meta.bg} border-l-4 ${meta.color.replace("text-", "border-")}`}>
                <div className={`mt-0.5 ${meta.color}`}>
                  <Icon className={`w-5 h-5 ${row.state === "processing" ? "animate-spin" : ""}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="text-sm font-bold text-slate-900">{row.status || "—"}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 border ${meta.color} bg-white`}>
                      {meta.label}
                    </span>
                    {row.notify === true && (
                      <span className="text-[10px] text-emerald-700 flex items-center gap-0.5">
                        <Bell className="w-3 h-3" /> notificatie
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600 flex flex-wrap gap-3">
                    {row.planned && <span><Calendar className="inline w-3 h-3 mr-1 -mt-0.5" />gepland {fmtDateTime(row.planned, cockpit.automation?.timezone)}</span>}
                    {row.source && <span className="text-slate-500">bron: {row.source}</span>}
                  </div>
                  {row.error && (
                    <div className="mt-1 text-[11px] text-red-700 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5" /> {row.error}
                    </div>
                  )}
                </div>
                {row.state === "failed" && canEdit && (
                  <Button size="sm" variant="ghost"
                          className="rounded-none text-[10px] text-amber-700 hover:bg-amber-50"
                          onClick={() => retryEvent(row.id)}
                          data-testid={`timeline-retry-${row.id}`}>
                    <PlayCircle className="w-3 h-3 mr-1" /> Opnieuw
                  </Button>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// §4 Automation Preview Dialog
// ═══════════════════════════════════════════════════════════════
function AutomationPreviewDialog({ open, onClose, preview }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="cockpit-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#082c50]">
            <Eye className="w-5 h-5" /> Automation Preview
          </DialogTitle>
          <DialogDescription>
            Dit is exact wat de automation zal uitvoeren wanneer je op Opslaan drukt. Er wordt nu <strong>nog niets gewijzigd</strong>.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="p-4 text-center text-slate-500 italic">Preview laden…</div>
        ) : (
          <div className="space-y-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <PreviewStat label="Template" value={preview.template_code || "—"} />
              <PreviewStat label="Mode" value={preview.mode?.toUpperCase() || "—"} />
              <PreviewStat label="Events" value={preview.events_count} testId="preview-events-count" />
              <PreviewStat label="Klanten" value={preview.customer_count} testId="preview-customer-count" />
              <PreviewStat label="HOLD geblokkeerd" value={preview.hold_shipments_blocked} tone="red"
                           testId="preview-hold-blocked" />
              <PreviewStat label="Uitgesloten" value={preview.excluded_shipments} tone="gray" />
              <PreviewStat label="Portal sync" value={preview.will_sync?.portal_updates ? "AAN" : "UIT"} />
              <PreviewStat label="Warehouse sync" value={preview.will_sync?.warehouse_sync ? "AAN" : "UIT"} />
            </div>

            <div className="border border-slate-200 bg-white">
              <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 text-[10px] uppercase font-bold text-slate-600">
                Planned events ({preview.events?.length || 0})
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Wanneer</th>
                      <th className="text-left p-2">Ontvangers</th>
                      <th className="text-right p-2">Klanten</th>
                      <th className="text-left p-2">Regel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(preview.events || []).map((ev, i) => (
                      <tr key={i} data-testid={`preview-event-${i}`}>
                        <td className="p-2 font-mono font-bold text-[#082c50]">{ev.new_status}</td>
                        <td className="p-2">{ev.scheduled_for ? fmtDateTime(ev.scheduled_for) : <span className="text-red-600 italic">{ev.will_skip_reason || "geen datum"}</span>}</td>
                        <td className="p-2 uppercase text-[10px] font-bold">
                          {RECIPIENT_LABELS[ev.notification_recipients] || ev.notification_recipients}
                        </td>
                        <td className="p-2 text-right font-mono">{ev.expected_customer_notifications || 0}</td>
                        <td className="p-2 text-[10px] text-slate-500">{ev.rule_type}{ev.offset_days != null ? ` (${ev.offset_days > 0 ? "+" : ""}${ev.offset_days}d)` : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-[10px] text-slate-500 italic">
              Gegenereerd: {fmtDateTime(preview.generated_at)}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none text-xs"
                  data-testid="cockpit-preview-close">
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function PreviewStat({ label, value, tone, testId }) {
  const cls = tone === "red" ? "bg-red-50 border-red-200 text-red-900"
            : tone === "gray" ? "bg-slate-100 border-slate-300 text-slate-700"
            : "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`border px-3 py-2 ${cls}`} data-testid={testId}>
      <div className="text-[9px] font-bold uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-sm font-bold">{value}</div>
    </div>
  );
}
